import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { GiftIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';

import { cn } from '@restworld/utils-common';
import { useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { getAssetSrc, customImageLoader, useAnalytics, useIntersectionObserver } from '@restworld/utils-common';

import { useOpenReferralInvite } from '../referral/use-open-referral-invite';
import { getReferralCredits, ReferralCredits } from '../referral/use-referral-credits';

type ReferralCardVariant = 'with_credits' | 'no_credits';

/**
 * Fires `referral_dashboard_card_viewed` once per mount when the card is at least
 * half visible — distinguishes "never seen" (below the fold) from "seen and ignored".
 */
function CardViewTracker({
  variant,
  availableCredits,
  children,
}: {
  variant: ReferralCardVariant;
  availableCredits: number;
  children: ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAnalytics();

  // Snapshot of the props at first-intersection time. Tracking the live props instead
  // would re-emit the event if a query refetch changes the credits while mounted.
  const latestPropsRef = useRef({ variant, availableCredits });
  useEffect(() => {
    latestPropsRef.current = { variant, availableCredits };
  }, [variant, availableCredits]);

  const [viewedSnapshot, setViewedSnapshot] = useState<{
    variant: ReferralCardVariant;
    availableCredits: number;
  } | null>(null);

  const observerOptions = useMemo(() => ({ threshold: 0.5 }), []);
  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      setViewedSnapshot((prev) => prev ?? { ...latestPropsRef.current });
    }
  }, []);

  useIntersectionObserver({ targetRef: cardRef, options: observerOptions, callback: onIntersect });

  useEffect(() => {
    if (!viewedSnapshot) return;
    trackEvent('referral_dashboard_card_viewed', {
      variant: viewedSnapshot.variant,
      available_credits: viewedSnapshot.availableCredits,
    });
    // trackEvent in deps re-fires the capture once PostHog init completes (see docs/claude/analytics.md)
  }, [viewedSnapshot, trackEvent]);

  return <div ref={cardRef}>{children}</div>;
}

/* --- Sub-components --- */

interface CardProps {
  onClick: () => void;
}

function WithCreditsCard({ onClick, availableCredits }: CardProps & { availableCredits: number }) {
  const { t } = useTranslation('dashboard-page');

  return (
    <WithCreditsCardContainer onClick={onClick}>
      <BackgroundImageWrapper>
        <Image
          src={getAssetSrc('/images/rw-logo-bg.png')}
          alt=""
          fill
          loader={customImageLoader}
          sizes="(max-width: 768px) 100vw, 640px"
          className="object-cover object-top"
        />
      </BackgroundImageWrapper>
      <CardContent gap={24}>
        <HighlightedBadge>{t('referral_badge_label')}</HighlightedBadge>
        <TextBlock>
          <WithCreditsText>{t('referral_credits_available', { count: availableCredits })}</WithCreditsText>
        </TextBlock>
      </CardContent>
      <DarkButtonWrapper>
        <FrostedCardButton>
          <GiftIcon width={20} height={20} color="#FFFFFF" />
          <PaddedButtonLabel>{t('referral_get_more_credits')}</PaddedButtonLabel>
        </FrostedCardButton>
      </DarkButtonWrapper>
    </WithCreditsCardContainer>
  );
}

function NoCreditsCard({ onClick, credits }: CardProps & { credits: ReferralCredits }) {
  const { t } = useTranslation('dashboard-page');

  return (
    <NoCreditsCardContainer onClick={onClick}>
      <BackgroundImageWrapper>
        <Image
          src={getAssetSrc('/images/rw-logo-bg.png')}
          alt=""
          fill
          loader={customImageLoader}
          sizes="(max-width: 768px) 100vw, 640px"
          className="object-cover object-top"
        />
      </BackgroundImageWrapper>
      <CardContent gap={20}>
        <RestworldBadge>{t('referral_badge_label')}</RestworldBadge>
        <TextBlock>
          <NoCreditsText>{t('referral_no_credits_line1', { invitee: credits.invitee })}</NoCreditsText>
          <NoCreditsText>{t('referral_no_credits_line2', { inviter: credits.inviter })}</NoCreditsText>
        </TextBlock>
      </CardContent>
      <GlowButtonWrapper>
        <FrostedCardButton>
          <GiftIcon width={20} height={20} color="#FFFFFF" />
          <PaddedButtonLabel>{t('referral_get_free_credits')}</PaddedButtonLabel>
        </FrostedCardButton>
      </GlowButtonWrapper>
    </NoCreditsCardContainer>
  );
}

function CardSkeleton() {
  return (
    <SkeletonContainer>
      <SkeletonBadge />
      <SkeletonLine style={{ width: '75%' }} />
      <SkeletonLine style={{ width: '50%' }} />
      <SkeletonButton />
    </SkeletonContainer>
  );
}

/* --- Main component --- */

function DashboardReferralCard() {
  const { user } = useAuth();
  const employersService = useEmployersService();
  const { trackEvent } = useAnalytics();
  const openReferral = useOpenReferralInvite();

  const { data, isLoading } = useQuery({
    queryKey: ['referral-link'],
    queryFn: async () => {
      const response = await employersService.getReferralLink();
      return response.data;
    },
    enabled: !!user?.using_ppu_business_model,
  });

  const availableCredits = data?.credits?.available_credits ?? 0;
  const variant: ReferralCardVariant = availableCredits > 0 ? 'with_credits' : 'no_credits';

  const handleClick = useCallback(() => {
    trackEvent('referral_dashboard_card_clicked', { variant, available_credits: availableCredits });
    openReferral('dashboard_card');
  }, [trackEvent, variant, availableCredits, openReferral]);

  if (!user?.using_ppu_business_model) {
    return null;
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  return (
    <CardViewTracker variant={variant} availableCredits={availableCredits}>
      {availableCredits > 0 ? (
        <WithCreditsCard onClick={handleClick} availableCredits={availableCredits} />
      ) : (
        <NoCreditsCard onClick={handleClick} credits={getReferralCredits(data)} />
      )}
    </CardViewTracker>
  );
}

export default DashboardReferralCard;

/* --- Shared styles --- */

const BaseCardContainer = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('relative flex flex-col justify-center gap-10 rounded-2xl overflow-hidden cursor-pointer', className)}
    style={{ padding: '40px 20px 20px', ...style }}
    {...props}
  />
);

const BackgroundImage = ({ className, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    className={cn('absolute top-0 left-0 w-full h-full pointer-events-none opacity-50', className)}
    style={{ borderRadius: '16px', objectFit: 'cover', objectPosition: 'center top' }}
    {...props}
  />
);

// Positioned container for a `next/image` `fill` background (the image fills this
// wrapper, which covers the card behind its content). Carries the rounding + muted
// opacity that the old `BackgroundImage` styled-component applied directly.
const BackgroundImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'absolute top-0 left-0 w-full h-full pointer-events-none opacity-50 overflow-hidden rounded-2xl',
      className
    )}
    {...props}
  />
);

const CardContent = ({ className, gap, ...props }: React.HTMLAttributes<HTMLDivElement> & { gap: number }) => (
  <div className={cn('relative flex flex-col w-full', className)} style={{ gap: `${gap}px` }} {...props} />
);

const TextBlock = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-2 w-full', className)} {...props} />
);

const BaseBadge = ({ className, style, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('inline-flex items-center justify-center self-start px-2 font-medium text-sm', className)}
    style={{ height: '24px', borderRadius: '4px', lineHeight: 1.21, ...style }}
    {...props}
  />
);

const ButtonWrapper = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('relative flex w-full', className)} style={{ borderRadius: '8px', ...style }} {...props} />
);

const CardButton = ({ className, style, children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cn('flex items-center justify-center gap-2 w-full', className)}
    style={{
      padding: '8px 16px',
      height: '48px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      ...style,
    }}
    {...props}
  >
    {children}
  </button>
);

const ButtonLabel = ({ className, style, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('font-semibold', className)}
    style={{ fontSize: '16px', color: '#fafafa', lineHeight: 1.21, ...style }}
    {...props}
  />
);

/* --- With Credits variant (light background) --- */

const WithCreditsCardContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <BaseCardContainer
    className={className}
    style={{
      background: '#f0ebf8',
      border: '1px solid #dedee1',
      boxShadow: '0px 2px 4px 0px rgba(0, 0, 26, 0.05)',
    }}
    {...props}
  />
);

const HighlightedBadge = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <BaseBadge className={className} style={{ background: '#6633bb', color: '#fafafa' }} {...props} />
);

const WithCreditsText = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('w-full font-praktikal-bold font-normal', className)}
    style={{ fontSize: '18px', lineHeight: 1.21, color: '#1c1c33' }}
    {...props}
  />
);

const DarkButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <ButtonWrapper
    className={className}
    style={{
      borderRadius: '8px',
      background: 'radial-gradient(circle at 2% 10%, rgba(72, 36, 133, 1) 0%, rgba(28, 28, 51, 1) 100%)',
    }}
    {...props}
  />
);

/* --- No Credits variant (dark gradient background) --- */

const NoCreditsCardContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <BaseCardContainer
    className={className}
    style={{
      background: 'radial-gradient(circle at 0% 100%, rgba(72, 36, 133, 1) 0%, rgba(0, 0, 26, 1) 50%)',
      border: '1px solid rgba(0, 0, 0, 1)',
    }}
    {...props}
  />
);

const RestworldBadge = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <BaseBadge className={className} style={{ background: '#f0ebf8', color: '#482485' }} {...props} />
);

const NoCreditsText = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('w-full font-praktikal-bold font-normal', className)}
    style={{ fontSize: '20px', lineHeight: 1.275, color: '#fafafa' }}
    {...props}
  />
);

const GlowButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <ButtonWrapper
    className={className}
    style={{
      borderRadius: '8px',
      background: 'radial-gradient(circle at 0% 1%, rgba(72, 36, 133, 1) 0%, rgba(240, 235, 248, 0.1) 100%)',
      boxShadow: '0px 0px 32px 0px rgba(102, 51, 187, 0.75)',
    }}
    {...props}
  />
);

const FrostedCardButton = ({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <CardButton
    className={className}
    style={{
      background: 'rgba(250, 250, 250, 0.1)',
      transition: 'background 0.2s ease',
    }}
    {...props}
  />
);

const PaddedButtonLabel = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <ButtonLabel
    className={className}
    style={{ padding: '0 8px', fontSize: '16px', color: '#fafafa', lineHeight: 1.21 }}
    {...props}
  />
);

/* --- Skeleton loading --- */

const shimmerKeyframes = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

const SkeletonBlock = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <>
    <style>{shimmerKeyframes}</style>
    <div
      className={cn(className)}
      style={{
        background: 'linear-gradient(90deg, #e8e0f0 25%, #f0ebf8 50%, #e8e0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s ease-in-out infinite',
        borderRadius: '4px',
        ...style,
      }}
      {...props}
    />
  </>
);

const SkeletonContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <BaseCardContainer
    className={className}
    style={{
      background: '#f0ebf8',
      border: '1px solid #dedee1',
      boxShadow: '0px 2px 4px 0px rgba(0, 0, 26, 0.05)',
      gap: '16px',
    }}
    {...props}
  />
);

const SkeletonBadge = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <SkeletonBlock className={className} style={{ width: '100px', height: '24px' }} {...props} />
);

const SkeletonLine = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <SkeletonBlock className={className} style={{ height: '20px', ...style }} {...props} />
);

const SkeletonButton = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <SkeletonBlock className={cn('w-full', className)} style={{ height: '48px', borderRadius: '8px' }} {...props} />
);
